import { useEffect, useState, type FormEvent } from "react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Label } from "../../components/ui/Label";
import { Textarea } from "../../components/ui/Textarea";
import { Sheet } from "../../components/ui/Sheet";
import { Badge } from "../../components/ui/Badge";
import { api, ApiException } from "../../lib/api";

interface Provider {
  id: string;
  name: string;
  specialty: string | null;
  facility_name: string | null;
  address: string | null;
  phone: string | null;
  notes: string | null;
  archived_at: string | null;
  pending_approval?: number;
}

interface ProvidersResponse {
  providers: Provider[];
}

interface FormState {
  name: string;
  specialty: string;
  facility_name: string;
  address: string;
  phone: string;
  notes: string;
}

const empty: FormState = { name: "", specialty: "", facility_name: "", address: "", phone: "", notes: "" };

export function ProvidersPanel({ circleId }: { circleId: string }) {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<Provider | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    const q = includeArchived ? "?include_archived=1" : "";
    const data = await api.get<ProvidersResponse>(`/api/circles/${circleId}/providers${q}`);
    setProviders(data.providers);
  }

  useEffect(() => {
    refresh().catch(() => {});
  }, [circleId, includeArchived]);

  function openAdd() {
    setEditing(null);
    setForm(empty);
    setError(null);
    setOpen(true);
  }

  function openEdit(p: Provider) {
    setEditing(p);
    setForm({
      name: p.name,
      specialty: p.specialty ?? "",
      facility_name: p.facility_name ?? "",
      address: p.address ?? "",
      phone: p.phone ?? "",
      notes: p.notes ?? "",
    });
    setError(null);
    setOpen(true);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      name: form.name,
      specialty: form.specialty || null,
      facility_name: form.facility_name || null,
      address: form.address || null,
      phone: form.phone || null,
      notes: form.notes || null,
    };
    try {
      if (editing) {
        await api.post(`/api/providers/${editing.id}`, payload);
      } else {
        await api.post(`/api/circles/${circleId}/providers`, payload);
      }
      setOpen(false);
      await refresh();
    } catch (err) {
      if (err instanceof ApiException && err.code === "duplicate") {
        if (confirm("A provider with this name and facility already exists. Add anyway?")) {
          try {
            await api.post(`/api/circles/${circleId}/providers`, { ...payload, force: true });
            setOpen(false);
            await refresh();
          } catch (err2) {
            setError(err2 instanceof ApiException ? err2.message : "Failed.");
          }
        }
      } else {
        setError(err instanceof ApiException ? err.message : "Failed.");
      }
    } finally {
      setBusy(false);
    }
  }

  async function archive(p: Provider) {
    const isPending = !!p.pending_approval;
    const verb = isPending ? "Reject" : "Archive";
    const msg = isPending
      ? `Reject and delete the proposal for ${p.name}?`
      : `Archive ${p.name}? They won't appear when creating new visits, but past visits keep their info.`;
    if (!confirm(msg)) return;
    try {
      await api.del(`/api/providers/${p.id}`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : `Failed to ${verb.toLowerCase()}.`);
    }
  }

  async function approvePending(p: Provider) {
    try {
      await api.post(`/api/providers/${p.id}/approve`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed to approve.");
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Providers</h2>
        <Button onClick={openAdd}>Add provider</Button>
      </div>

      {error && (
        <div className="mt-3 rounded-md bg-red-50 border border-red-200 text-danger text-sm px-3 py-2">{error}</div>
      )}

      <label className="mt-4 flex items-center gap-2 text-sm text-text-muted">
        <input
          type="checkbox"
          checked={includeArchived}
          onChange={(e) => setIncludeArchived(e.target.checked)}
          className="h-4 w-4"
        />
        Show archived
      </label>

      {providers.length === 0 ? (
        <p className="mt-6 text-sm text-text-muted">
          No providers yet. Add the loved one's doctors so they're one tap away when creating visits.
        </p>
      ) : (
        <ul className="mt-4 divide-y divide-border">
          {providers.map((p) => (
            <li key={p.id} className="py-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-2 flex-wrap">
                  <span className="truncate">{p.name}</span>
                  {p.pending_approval ? <Badge tone="warning">Pending</Badge> : null}
                  {p.archived_at && <Badge tone="neutral">archived</Badge>}
                </div>
                <div className="text-sm text-text-muted truncate">
                  {[p.specialty, p.facility_name].filter(Boolean).join(" · ") || "—"}
                </div>
                {p.address && <div className="text-xs text-text-muted truncate">{p.address.split("\n")[0]}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                {p.pending_approval ? (
                  <>
                    <Button size="md" onClick={() => approvePending(p)}>Approve</Button>
                    <Button variant="ghost" size="md" onClick={() => archive(p)} className="text-danger">Reject</Button>
                  </>
                ) : (
                  <>
                    <Button variant="ghost" size="md" onClick={() => openEdit(p)}>Edit</Button>
                    {!p.archived_at && (
                      <Button variant="ghost" size="md" onClick={() => archive(p)} className="text-danger">
                        Archive
                      </Button>
                    )}
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit provider" : "Add provider"}
        description={editing ? "Edits don't affect past visits — their details are snapshotted." : undefined}
      >
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="p-name">Name</Label>
            <Input id="p-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dr. N Vallala" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="p-spec">Specialty</Label>
              <Input id="p-spec" value={form.specialty} onChange={(e) => setForm({ ...form, specialty: e.target.value })} placeholder="Heart and Vascular" />
            </div>
            <div>
              <Label htmlFor="p-fac">Facility</Label>
              <Input id="p-fac" value={form.facility_name} onChange={(e) => setForm({ ...form, facility_name: e.target.value })} placeholder="Mercy Heart Hospital" />
            </div>
          </div>
          <div>
            <Label htmlFor="p-addr">Address</Label>
            <Textarea id="p-addr" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="625 S New Ballas Rd, Ste 2015&#10;Saint Louis, MO 63141" />
          </div>
          <div>
            <Label htmlFor="p-phone">Phone</Label>
            <Input id="p-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="314-251-1700" />
          </div>
          <div>
            <Label htmlFor="p-notes">Notes</Label>
            <Textarea id="p-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Parking, accessibility, anything useful" />
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="submit" loading={busy}>{editing ? "Save changes" : "Add provider"}</Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </form>
      </Sheet>
    </div>
  );
}
